import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, HStack, Icon, Modal, Text, VStack } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useIsMobile } from "@repo/utils";
import {
  cancelGeniusBackTrapExit,
  resetGeniusDeeplinkBackTrap,
  sealGeniusDeeplinkHistory,
} from "./geniusDeeplinkBackTrap";
import {
  clearGeniusLogoutConfirmPending,
  subscribeGeniusLogoutConfirm,
  subscribeGeniusLogoutConfirmDismiss,
} from "./geniusLogoutConfirm";
import {
  getGeniusDeeplinkLogoutRedirectPath,
  logoutGeniusDeeplinkRestrictedSession,
} from "./logoutGeniusDeeplinkRestrictedSession";
import styles from "./GeniusLogoutConfirmHost.module.scss";

export function GeniusLogoutConfirmHost() {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const modalStyles = useMemo(
    () => ({
      modal: {
        borderTopLeftRadius: isMobile ? "2.5rem" : "1.5rem",
        borderTopRightRadius: isMobile ? "2.5rem" : "1.5rem",
        borderBottomLeftRadius: isMobile ? "0px" : "1.5rem",
        borderBottomRightRadius: isMobile ? "0px" : "1.5rem",
        overflow: "hidden" as const,
      },
      body: {
        flex: "0 0 auto" as const,
        padding: 0,
      },
    }),
    [isMobile],
  );

  useEffect(() => {
    const unsubRequest = subscribeGeniusLogoutConfirm(() => {
      setOpen(true);
    });
    const unsubDismiss = subscribeGeniusLogoutConfirmDismiss(() => {
      setOpen(false);
    });
    return () => {
      unsubRequest();
      unsubDismiss();
    };
  }, []);

  const handleStay = useCallback(() => {
    clearGeniusLogoutConfirmPending();
    cancelGeniusBackTrapExit();
    sealGeniusDeeplinkHistory();
    setOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    clearGeniusLogoutConfirmPending();
    setOpen(false);
    resetGeniusDeeplinkBackTrap();
    logoutGeniusDeeplinkRestrictedSession();
    window.location.assign(getGeniusDeeplinkLogoutRedirectPath());
  }, []);

  const actionButtonColors = useMemo(
    () => ({
      backgroundColor: theme.colors.primary[30],
      textColor: theme.colors.background[50],
    }),
    [theme.colors.background, theme.colors.primary],
  );

  const header = (
    <HStack className={styles.header}>
      <Button variant="ghost" onPress={handleStay} aria-label="Close">
        <Icon name="close" color={theme.colors.primary[40]} size={20} />
      </Button>
    </HStack>
  );

  const body = (
    <VStack className={styles.body}>
      <Box className={styles.iconWrap} aria-hidden>
        <Icon name="LogOutIcon" size={28} color={theme.colors.primary[40]} />
      </Box>

      <VStack className={styles.textBlock}>
        <Text className={styles.title}>Log out?</Text>
        <Text className={styles.subtitle}>
          Going back will sign you out of your account. You can sign in again
          from the login page when you&apos;re ready.
        </Text>
      </VStack>

      <VStack className={styles.actions}>
        <Button
          variant="primary"
          className={styles.actionBtn}
          backgroundColor={actionButtonColors.backgroundColor}
          textColor={actionButtonColors.textColor}
          onPress={handleLogout}
        >
          <Text className={styles.actionBtnText}>Log out</Text>
        </Button>
        <Button
          variant="primary"
          className={styles.actionBtn}
          backgroundColor={actionButtonColors.backgroundColor}
          textColor={actionButtonColors.textColor}
          onPress={handleStay}
        >
          <Text className={styles.actionBtnText}>Stay here</Text>
        </Button>
      </VStack>
    </VStack>
  );

  return (
    <Modal
      isModalOpen={open}
      onClose={handleStay}
      width={isMobile ? "100%" : "31rem"}
      height="auto"
      customHeader={header}
      showFooter={false}
      body={body}
      overLayColor={theme.colors.overlay}
      styles={modalStyles}
    />
  );
}
